import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { describeError } from "../auth/authErrors";
import { type CognitoAuth, SignInError } from "../auth/cognitoTokenProvider";
import { useI18n } from "../i18n/I18n";
import { SignInPage } from "./SignInPage";

/**
 * `/auth/callback` — redeems the authorization code and gets out of the way.
 *
 * On success it replaces the history entry, so the browser's back button does
 * not land on a URL holding a spent code.
 */
export function CallbackPage({
  auth,
  onSignedIn,
}: {
  auth: CognitoAuth;
  onSignedIn: () => void;
}) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [error, setError] = useState<string | null>(null);
  // StrictMode mounts effects twice in development; a code may only be
  // redeemed once, so the second run must not make the request.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;

    auth
      .completeSignIn(window.location.search)
      .then(({ returnTo }) => {
        onSignedIn();
        navigate(returnTo, { replace: true });
      })
      .catch((caught: unknown) =>
        setError(
          caught instanceof SignInError
            ? t(`auth.error.${caught.code}`, caught.params)
            : describeError(caught, t),
        ),
      );
  }, [auth, navigate, onSignedIn, t]);

  if (error !== null) {
    return <SignInPage message={error} />;
  }
  return (
    <div className="app-signin">
      <div className="ds-notice --light --center">
        <p>{t("auth.signingIn")}</p>
      </div>
    </div>
  );
}
